import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Search, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { AdminNavbar } from '../../components/custom/AdminNavbar';
import { CategoryPill } from '../../components/custom/CategoryPill';
import { MenuGrid, MenuItemDialog, CategoryDialog, BulkActionsToolbar, ExportMenuDialog } from '../../components/admin';
import { useAuth } from '../../context/AuthContext';
import { menuAPI } from '../../utils/api';
import { toast } from 'sonner';

const INITIAL_FORM_DATA = {
  name: '',
  description: '',
  price: '',
  category: '',
  subcategory: '',
  image_url: '',
  available: true,
  customization_options: [],
  item_type: 'standard',
  bundle_config: null,
};

const ALL = 'all';
const UNASSIGNED = '__unassigned__';

const AdminMenu = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  
  // Bulk selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Search & filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(ALL);

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/admin/login');
      return;
    }
    fetchMenu();
    fetchCategories();
  }, [user, navigate]);

  const fetchMenu = async () => {
    try {
      const response = await menuAPI.getAllMenuItems();
      setMenuItems(response.data);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
      toast.error('Failed to load menu');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await menuAPI.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (editingItem) {
        await menuAPI.updateMenuItem(editingItem.id, data);
        setMenuItems(menuItems.map(item => 
          item.id === editingItem.id ? { ...item, ...data } : item
        ));
        toast.success('Item updated successfully');
      } else {
        const response = await menuAPI.createMenuItem(data);
        setMenuItems([...menuItems, response.data]);
        toast.success('Item created successfully');
      }

      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save item:', error);
      toast.error('Failed to save item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price.toString(),
      category: item.category,
      subcategory: item.subcategory || '',
      image_url: item.image_url || '',
      available: item.available,
      customization_options: item.customization_options || [],
      item_type: item.item_type || 'standard',
      bundle_config: item.bundle_config || null,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await menuAPI.deleteMenuItem(itemId);
      setMenuItems(menuItems.filter(item => item.id !== itemId));
      toast.success('Item deleted successfully');
    } catch (error) {
      console.error('Failed to delete item:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleDuplicate = async (item) => {
    try {
      const payload = {
        name: `${item.name} (Copy)`,
        description: item.description || '',
        price: item.price,
        category: item.category,
        subcategory: item.subcategory || null,
        image_url: item.image_url || null,
        available: item.available !== false,
        sort_order: item.sort_order ?? 0,
        customization_options: item.customization_options || [],
        item_type: item.item_type || 'standard',
        bundle_config: item.bundle_config || null,
      };
      const response = await menuAPI.createMenuItem(payload);
      const newItem = response.data;
      setMenuItems((prev) => [...prev, newItem]);
      toast.success('Item duplicated — edit the copy to customise it');
      // Immediately open edit dialog on the new copy so admin can tweak it.
      handleEdit(newItem);
    } catch (error) {
      console.error('Failed to duplicate item:', error);
      toast.error('Failed to duplicate item');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData(INITIAL_FORM_DATA);
  };

  // Apply search + category filter to derive the visible items.
  // NOTE: useMemo must run unconditionally before any early return below.
  const filteredMenuItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return menuItems.filter((item) => {
      // Category filter
      if (categoryFilter === UNASSIGNED) {
        if (item.category) return false;
      } else if (categoryFilter !== ALL) {
        if (item.category !== categoryFilter) return false;
      }
      // Search filter (name + description)
      if (q) {
        const hay = `${item.name || ''} ${item.description || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [menuItems, searchTerm, categoryFilter]);

  const filterActive = searchTerm.trim() !== '' || categoryFilter !== ALL;

  const handleResetFilters = () => {
    setSearchTerm('');
    setCategoryFilter(ALL);
  };

  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-background">
      <AdminNavbar title="Menu Management" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <Link to="/admin/dashboard">
            <Button variant="ghost">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <BulkActionsToolbar
              menuItems={menuItems}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              onRefetch={fetchMenu}
              selectionMode={selectionMode}
              setSelectionMode={setSelectionMode}
            />
            
            {!selectionMode && (
              <>
                <Button variant="outline" size="icon" onClick={fetchMenu} className="rounded-full">
                  <RefreshCw className="h-4 w-4" />
                </Button>

                <ExportMenuDialog
                  menuItems={menuItems}
                  filteredMenuItems={filteredMenuItems}
                  filterActive={filterActive}
                  categoryFilterLabel={
                    categoryFilter === ALL
                      ? 'All'
                      : categoryFilter === UNASSIGNED
                        ? 'Unassigned'
                        : categoryFilter
                  }
                />

                <CategoryDialog 
                  categories={categories} 
                  onUpdate={fetchCategories} 
                />

                <MenuItemDialog
                  open={dialogOpen}
                  onOpenChange={setDialogOpen}
                  editingItem={editingItem}
                  formData={formData}
                  setFormData={setFormData}
                  categories={categories}
                  onSubmit={handleSubmit}
                  onReset={resetForm}
                />
              </>
            )}
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          {selectionMode 
            ? 'Click items to select them for bulk actions.' 
            : filterActive
              ? 'Filtered view — drag-to-reorder is disabled. Clear filters to reorder.'
              : 'Drag items to reorder them. Changes are saved automatically.'}
        </p>

        {/* Search & Category Tabs */}
        {!selectionMode && (
          <div className="bg-white border rounded-2xl p-4 mb-4 space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="menu-search-input"
                placeholder="Search items by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Category Pill Tabs (matches customer menu style) */}
            <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
              <div className="flex gap-2 pb-1" data-testid="menu-category-tabs">
                <CategoryPill
                  category="All"
                  active={categoryFilter === ALL}
                  onClick={() => setCategoryFilter(ALL)}
                />
                {categories.map((c) => (
                  <CategoryPill
                    key={c.id}
                    category={c.name}
                    active={categoryFilter === c.name}
                    onClick={() => setCategoryFilter(c.name)}
                  />
                ))}
                <CategoryPill
                  category="Unassigned"
                  active={categoryFilter === UNASSIGNED}
                  onClick={() => setCategoryFilter(UNASSIGNED)}
                />
              </div>
            </div>

            {filterActive && (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetFilters}
                  data-testid="menu-reset-filters"
                  className="whitespace-nowrap"
                >
                  <X className="h-4 w-4 mr-1.5" />
                  Reset filters
                </Button>
              </div>
            )}
          </div>
        )}

        {!loading && !selectionMode && filterActive && (
          <p
            className="text-xs text-muted-foreground mb-3"
            data-testid="menu-filter-count"
          >
            Showing {filteredMenuItems.length} of {menuItems.length} items
          </p>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white rounded-2xl h-64 animate-pulse" />
            ))}
          </div>
        ) : (
          <MenuGrid
            menuItems={filteredMenuItems}
            setMenuItems={setMenuItems}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onRefetch={fetchMenu}
            selectionMode={selectionMode}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            reorderDisabled={filterActive}
          />
        )}
      </div>
    </div>
  );
};

export default AdminMenu;
